import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import {
  Edit,
  XCircle as XCircleIcon,
  X,
  AlertTriangle,
  CheckCircle,
  Search,
  Filter,
  Plus,
  Users,
  Eye,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  User,
} from "lucide-react";
import customerGroupService from "../../../services/customerGroupService";
import { CustomerGroup } from "../../../types/customerGroup";
import { LoadingSpinner } from "../../../shared/LoadingSpinner";
import Select from "../../../shared/components/form/Select";
import { BRAND_COLORS } from "../../../shared/constants/brandColors";
import { userService } from "../../../services/userService";

// Enhanced ToggleStatusCell Component
interface ToggleStatusCellProps {
  customerGroup: CustomerGroup;
  onRefresh: () => void;
}

const ToggleStatusCell: React.FC<ToggleStatusCellProps> = ({ customerGroup, onRefresh }) => {
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });

  const handleToggle = async () => {
    setIsLoading(true);
    try {
      if (customerGroup.status === "active") {
        await customerGroupService.deactivateCustomerGroup(customerGroup._id);
        toast.success("Customer Group deactivated successfully");
      } else {
        await customerGroupService.activateCustomerGroup(customerGroup._id);
        toast.success("Customer Group activated successfully");
      }
      setConfirmOpen(false);
      onRefresh();
    } catch (err: unknown) {
      console.error("Toggle error:", err);
      toast.error(`Failed to ${customerGroup.status === "active" ? "deactivate" : "activate"} customer group`);
    } finally {
      setIsLoading(false);
    }
  };

  const isActive = customerGroup.status === "active";

  const handleOpenModal = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setClickPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    });
    setConfirmOpen(true);
  };

  return (
    <>
      <button
        onClick={handleOpenModal}
        title={`Click to ${isActive ? "Deactivate" : "Activate"}`}
        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${
          isActive 
            ? "bg-green-50 border-green-200 hover:bg-green-100" 
            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
        }`}
      >
        <div
          className={`w-3 h-3 rounded-full transform transition-all duration-300 ${
            isActive 
              ? "translate-x-2 bg-green-500" 
              : "-translate-x-2 bg-gray-400"
          }`}
        />
      </button>

      <AnimatePresence>
        {isConfirmOpen && (
          <div className="fixed inset-0 z-90 overflow-y-auto bg-gray-500/5">
            <div className="flex min-h-full items-center justify-end p-4">
              <motion.div
                initial={{ 
                  opacity: 0, 
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y,
                  z: -100
                }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  x: -20,
                  y: 0,
                  z: 0
                }}
                exit={{ 
                  opacity: 0, 
                  scale: 0.95,
                  x: clickPosition.x,
                  y: clickPosition.y, 
                  z: -100
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                style={{
                  transform: 'translateZ(50px)',
                  perspective: '1000px',
                  transformOrigin: 'right'
                }}
                className="relative w-full max-w-lg bg-white rounded-lg border border-gray-200 shadow-[0_8px_30px_rgb(0,0,0,0.12)] mr-4"
              >
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${
                      isActive ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
                    }`}>
                      {isActive ? (
                        <XCircleIcon size={20} className="text-red-500" />
                      ) : (
                        <CheckCircle size={20} className="text-green-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">
                        {isActive ? "Deactivate Customer Group" : "Activate Customer Group"}
                      </h2>
                      <p className="text-sm text-gray-600 break-words">
                        {customerGroup.customerGroupName}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setConfirmOpen(false)}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200 transition-all duration-200"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div className="p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200 flex-shrink-0">
                      <AlertTriangle size={14} className="text-amber-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                        Are you sure you want to{" "}
                        <span className="font-medium text-gray-900">
                          {isActive ? "deactivate" : "activate"}
                        </span>{" "}
                        <span className="font-medium break-words" style={{ color: BRAND_COLORS.primary }}>{customerGroup.customerGroupName}</span>?
                      </p>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-600 leading-relaxed">
                          {isActive 
                            ? "This customer group will no longer be available for selection."
                            : "This customer group will become available for selection."
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleToggle}
                    disabled={isLoading}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 ${
                      isActive
                        ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                        : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                    } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span>
                        {isActive ? "Deactivate" : "Activate"}
                      </span>
                    )}
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

interface Props {
  data: CustomerGroup[];
  search: string;
  setSearch: (val: string) => void;
  status: string | null;
  setStatus: (val: string | null) => void;
  page: number;
  setPage: (val: number) => void;
  totalItems: number;
  itemsPerPage: number;
  onEdit?: (customerGroup: CustomerGroup) => void;
  onView?: (customerGroup: CustomerGroup) => void;
  onAdd?: () => void;
  onRefresh: () => void;
  isLoading?: boolean;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
}

export default function CustomerGroupTable({
  data: customerGroups,
  search,
  setSearch,
  status,
  setStatus,
  page,
  setPage,
  totalItems,
  itemsPerPage,
  onEdit,
  onView,
  onAdd,
  onRefresh,
  isLoading = false,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const sortMenuRef = useRef<HTMLDivElement>(null);
  const { control } = useForm({});
  const [showFilters, setShowFilters] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  // Close sort menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
        setSortMenuOpen(false);
      }
    };

    if (sortMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortMenuOpen]);

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  // Fetch user names for createdBy
  useEffect(() => {
    const fetchUserNames = async () => {
      const userIds = new Set<string>();
      customerGroups.forEach(customerGroup => {
        if (customerGroup.createdBy) userIds.add(customerGroup.createdBy);
      });

      if (userIds.size === 0) return;

      try {
        const userPromises = Array.from(userIds).map(async (userId) => {
          try {
            const user = await userService.getUserById(userId);
            return { id: userId, name: user.name };
          } catch (error) {
            console.error(`Failed to fetch user ${userId}:`, error);
            return { id: userId, name: 'Unknown User' };
          }
        });

        const userResults = await Promise.all(userPromises);
        const nameMap: Record<string, string> = {};
        userResults.forEach(({ id, name }) => {
          nameMap[id] = name;
        });
        setUserNames(nameMap);
      } catch (error) {
        console.error('Error fetching user names:', error);
      }
    };

    if (customerGroups.length > 0) {
      fetchUserNames();
    }
  }, [customerGroups]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleResetFilters = () => {
    setSearch("");
    setStatus(null);
    setPage(1);
    inputRef.current?.focus();
  };

  const getUserName = (userId: string) => {
    return userNames[userId] || 'Loading...';
  };

  const getCustomerCount = (customerGroup: CustomerGroup) => {
    return customerGroup.customerId?.length || 0;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-white to-gray-25">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search customer groups... (Ctrl+K)"
                className="block w-full sm:w-80 pl-9 pr-4 py-2.5 text-sm font-medium border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all duration-200 bg-white"
                style={{ 
                  '--tw-ring-color': BRAND_COLORS.primary,
                  'focusBorderColor': BRAND_COLORS.primary 
                } as React.CSSProperties}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 border ${
                showFilters
                  ? `text-white border-transparent`
                  : "text-gray-600 bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400"
              }`}
              style={showFilters ? { 
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})` 
              } : {}}
            >
              <Filter size={16} />
              Filters
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* Sort Button */}
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setSortMenuOpen(!sortMenuOpen)}
                className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 transition-all duration-200"
                style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
              >
                <ArrowUpDown size={16} />
                Sort
              </button>

              {/* Sort Dropdown */}
              <AnimatePresence>
                {sortMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full right-0 mt-2 z-20 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div className="p-3">
                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2">Sort by Name</div>
                      <button
                        onClick={() => {
                          setSortField("customerGroupName");
                          setSortOrder("asc");
                          setSortMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "customerGroupName" && sortOrder === "asc"
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700"
                        }`}
                      >
                        <ArrowUp size={14} />
                        Name (A-Z)
                      </button>
                      <button
                        onClick={() => {
                          setSortField("customerGroupName");
                          setSortOrder("desc");
                          setSortMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "customerGroupName" && sortOrder === "desc"
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700"
                        }`}
                      >
                        <ArrowDown size={14} />
                        Name (Z-A)
                      </button>

                      <div className="text-xs font-medium text-gray-500 px-2 py-1 mb-2 mt-3">Sort by Date</div>
                      <button
                        onClick={() => {
                          setSortField("createdAt");
                          setSortOrder("desc");
                          setSortMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "createdAt" && sortOrder === "desc"
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700"
                        }`}
                      >
                        <ArrowDown size={14} />
                        Newest First
                      </button>
                      <button
                        onClick={() => {
                          setSortField("createdAt");
                          setSortOrder("asc");
                          setSortMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-gray-50 transition ${
                          sortField === "createdAt" && sortOrder === "asc"
                            ? "bg-blue-50 text-blue-700"
                            : "text-gray-700"
                        }`}
                      >
                        <ArrowUp size={14} />
                        Oldest First
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={onAdd}
              className="inline-flex items-center gap-3 px-5 py-2.5 text-sm font-medium text-white border border-transparent rounded-xl shadow-lg shadow-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-emerald-200"
              style={{ 
                background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                '--tw-ring-color': BRAND_COLORS.primary 
              } as React.CSSProperties}
            >
              <div className="relative">
                <Plus size={16} className="absolute animate-ping opacity-40" />
                <Plus size={16} className="relative" />
              </div>
              <span>Add Customer Group</span>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-4"
            >
              <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-lg border border-gray-200">
                <div className="flex-1 min-w-[180px]">
                  <Controller
                    name="status"
                    control={control}
                    render={() => (
                      <Select
                        options={statusOptions}
                        placeholder="Filter by Status"
                        value={statusOptions.find(opt => opt.value === status)}
                        onChange={selectedOption => {
                          setStatus(selectedOption?.value || "");
                          setPage(1);
                        }}
                      />
                    )}
                  />
                </div>

                {status && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 hover:text-gray-700 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1"
                    style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                  >
                    <X size={14} />
                    Clear
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide">
                <div className="flex items-center gap-2">
                  <Users size={14} />
                  <span>Customer Group Name</span>
                </div>
              </th>
              <th className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide">
                <div className="flex items-center gap-2">
                  <Users size={14} />
                  <span>Customers</span>
                </div>
              </th>
              <th className="p-4 text-left text-sm font-medium text-gray-700 tracking-wide">
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span>Created By</span>
                </div>
              </th>
              <th className="p-4 text-center text-sm font-medium text-gray-700 tracking-wide">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <AnimatePresence>
              {isLoading ? (
                <motion.tr
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <td colSpan={4} className="p-16 text-center">
                    <LoadingSpinner />
                  </td>
                </motion.tr>
              ) : customerGroups.length === 0 ? (
                <motion.tr
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <td colSpan={4} className="p-16 text-center">
                    <div className="w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6 border border-gray-200"
                      style={{ background: `linear-gradient(135deg, ${BRAND_COLORS.primary}20, ${BRAND_COLORS.primary}40)` }}
                    >
                      <Users size={24} className="text-gray-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">No customer groups found</h3>
                    <p className="text-gray-600 mb-8 max-w-md mx-auto leading-relaxed">
                      {search || status
                        ? "Try adjusting your search or filter criteria to find the customer groups you're looking for"
                        : "Get started by creating your first customer group"}
                    </p>
                    {!search && !status && (
                      <button
                        onClick={onAdd}
                        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white border border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:-translate-y-0.5"
                        style={{ 
                          background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                          '--tw-ring-color': BRAND_COLORS.primary 
                        } as React.CSSProperties}
                      >
                        <Plus size={16} />
                        Add First Customer Group
                      </button>
                    )}
                  </td>
                </motion.tr>
              ) : (
                customerGroups.map((customerGroup, index) => {
                  return (
                    <motion.tr
                      key={customerGroup._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className={`transition-all duration-200 ${
                        customerGroup.status !== "active" 
                          ? "bg-gray-50/50 opacity-60 hover:bg-gray-100/60" 
                          : "hover:bg-gradient-to-r hover:from-gray-25 hover:to-gray-50"
                      }`}
                    >
                      {/* Customer Group Name */}
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center border border-gray-300">
                              <Users size={16} className="text-gray-600" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className={`text-sm font-medium truncate ${
                              customerGroup.status !== "active" ? "text-gray-500 line-through" : "text-gray-900"
                            }`}>
                              {customerGroup.customerGroupName}
                            </span>
                          </div>
                        </div>
                      </td>
                      
                      {/* Customers */}
                      <td className="p-4">
                        <button
                          className={`inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors duration-200 ${customerGroup.status !== "active"
                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                            : "bg-green-50 text-green-600 border-green-200 hover:bg-green-100"
                          }`}
                          title={customerGroup.status !== "active" ? "Customer group is inactive" : "Number of customers in this group"}
                          disabled={customerGroup.status !== "active"}
                        >
                          <Users size={12} />
                          {getCustomerCount(customerGroup)} customer{getCustomerCount(customerGroup) !== 1 ? 's' : ''}
                        </button>
                      </td>
                      
                      {/* Created By */}
                      <td className="p-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-1">
                            <User size={14} className={customerGroup.status !== "active" ? "text-gray-300" : "text-gray-400"} />
                            <span className={`text-sm font-medium ${customerGroup.status !== "active" ? "text-gray-500" : "text-gray-900"}`}>
                              {customerGroup.createdBy ? getUserName(customerGroup.createdBy) : 'N/A'}
                            </span>
                          </div>
                          {customerGroup.createdAt && (
                            <>
                              <span className={`text-xs ${customerGroup.status !== "active" ? "text-gray-400" : "text-gray-500"}`}>
                                {new Date(customerGroup.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </span>
                              <span className={`text-xs ${customerGroup.status !== "active" ? "text-gray-400" : "text-gray-400"}`}>
                                {new Date(customerGroup.createdAt).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                            </span>
                            </>
                          )}
                          </div>
                      </td>

                      {/* Actions */}
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-3">

                        <ToggleStatusCell customerGroup={customerGroup} onRefresh={onRefresh} />

                          <button
                            onClick={() => onView?.(customerGroup)}
                            title="View Customer Group Details"
                            className={`transition-colors duration-200 ${
                              customerGroup.status !== "active" 
                                ? "text-gray-300 hover:text-gray-400" 
                                : "text-gray-400 hover:text-blue-500"
                            }`}
                          >
                            <Eye size={18} />
                          </button>

                          <button
                            onClick={() => onEdit?.(customerGroup)}
                            title="Edit Customer Group"
                            className={`transition-colors duration-200 ${
                              customerGroup.status !== "active" 
                                ? "text-gray-300 hover:text-gray-400" 
                                : "text-gray-400 hover:text-emerald-400"
                            }`}
                          >
                            <Edit size={18} />
                          </button>

                        </div>
                      </td>
                    </motion.tr>
                  );
                })
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {customerGroups.length > 0 && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gradient-to-r from-gray-25 to-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 font-medium">
              Showing <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min((page - 1) * itemsPerPage + 1, totalItems)}</span> to{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{Math.min(page * itemsPerPage, totalItems)}</span> of{" "}
              <span className="font-semibold" style={{ color: BRAND_COLORS.primary }}>{totalItems}</span> customer groups
            </div>
            
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(page - 1, 1))}
                  disabled={page === 1}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                >
                  Previous
                </button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`inline-flex items-center justify-center w-8 h-8 text-sm font-medium rounded-md transition-all duration-200 ${
                          page === pageNum
                            ? "text-white border border-transparent"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                        }`}
                        style={page === pageNum ? { 
                          background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})` 
                        } : {}}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setPage(Math.min(page + 1, totalPages))}
                  disabled={page === totalPages}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  style={{ '--tw-ring-color': BRAND_COLORS.primary } as React.CSSProperties}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
}
